import React, { useEffect, useRef, useState } from 'react';
import { X, FileText, Upload, Save } from 'lucide-react';
import UploadOptionsModal from './UploadOptionsModal';
import PendientesRegistroModal from './PendientesRegistroModal';
import { useDocumentosByPersona, useRegisterDocumentoExistente, useUploadDocumento } from '../../hooks/useDocumentos';
import { createDocumentoFormData, validateDocumentoData } from '../../hooks/useDocumentos';
import { compressImageFile, isCompressibleImage } from '../../utils/imageCompressor';
import { CreateDocumentoData } from '../../types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import useClientePrerequisitos from '../../hooks/useClientePrerequisitos';
import apiService from '../../services/api';
import { FILE_CONFIG } from '../../config/api';
import { toast } from 'react-toastify';

interface SubirDocumentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  rutPersona: string;
  nombrePersona: string;
  // Prefill options when opening from a prerrequisito
  initialClientId?: number | null;
  initialClientPrereq?: number | string | null;
  initialNombreDocumento?: string;
  initialTipoDocumento?: string;
  // Reemplazo (como en Pre-SISI): el documento vigente que esta subida reemplaza. Tras subir,
  // pasa al historial y sus matches/tipos se mueven al nuevo (POST /documentos/:id/archivar-anterior).
  reemplazaDocumentoId?: number | null;
  reemplazaNombre?: string;
}

const SubirDocumentoModal: React.FC<SubirDocumentoModalProps> = ({ isOpen, onClose, onSuccess, rutPersona, nombrePersona, initialClientId = null, initialClientPrereq = null, initialNombreDocumento, initialTipoDocumento, reemplazaDocumentoId = null, reemplazaNombre }) => {
  const initialFormState = () => ({
    nombre_documento: '',
    tipo_documento: '',
    archivo: null as File | null,
    fecha_emision_documento: '',
    fecha_vencimiento_documento: '',
    fecha_vencimiento_indefinido: false,
    dias_validez_documento: '',
    estado_documento: '',
    institucion_emisora: '',
  });

  const [formData, setFormData] = useState(initialFormState());
  const [errors, setErrors] = useState<string[]>([]);
  // El cliente y el requisito ya no se eligen acá: vienen del lugar desde donde se abrió la
  // ventana (la tarjeta de un requisito). Lo que decide qué acredita el documento es el TIPO,
  // que vale en todos los clientes a la vez.
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClientPrereq, setSelectedClientPrereq] = useState<number | string | null>(null);
  // Only requisitos documents are supported now (valor interno 'prerrequisitos' mantenido por compatibilidad)
  const [uploadCategory] = useState<'personal' | 'prerrequisitos'>('prerrequisitos');
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showPendientesModal, setShowPendientesModal] = useState(false);
  const [selectedPendiente, setSelectedPendiente] = useState<{ file: any; displayName: string } | null>(null);
  // El tipo del catálogo ("¿Qué documento es?") ya no se pide al subir: el documento se guarda
  // sin tipo y no se manda `tipo_slug` (decisión de Claudio, 2026-10-13).

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const uploadMutation = useUploadDocumento(setUploadProgress);
  const registerExistingMutation = useRegisterDocumentoExistente();
  const { data: documentosPersonaData, refetch: refetchDocumentosPersona } = useDocumentosByPersona(rutPersona);
  const documentosData: any = (documentosPersonaData as any)?.data || {};
  const split = documentosData.documentos_locales_split || null;
  // Para la ventana de subir documentos (general), mostrar sólo los documentos comunes (no cursos)
  const documentosLocales: any[] = split ? (split.documentos || []) : (documentosData.documentos_locales || []);
  // Documentos ya registrados en la aplicación (puede venir en data.documentos o ser la propia data cuando es un array)
  const existingDocs: any[] = documentosData.documentos || (Array.isArray(documentosData) ? documentosData : []);

  const clientePrereqQuery = useClientePrerequisitos(selectedClientId ?? undefined);
  // Normalizar múltiples formas que puede devolver la API:
  // - [] (array directo)
  // - { success: true, data: [ ... ] }
  // - { success: true, data: { requisitos: [ ... ] } }
  // - { success: true, data: { data: [ ... ] } }
  // Además añadir un log temporal para facilitar depuración en entorno local.
  const clientePrereqs: any[] = (() => {
    const raw = (clientePrereqQuery as any).data || clientePrereqQuery || null;
    // Debug: mostrar la forma recibida (solo en desarrollo)
    try {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== 'production') console.debug('Requisitos cliente - raw:', selectedClientId, raw);
    } catch (e) {}

    if (!raw) return [];
    // Si es un array directo
    if (Array.isArray(raw)) return raw;
    // Si viene envuelto: { success, data }
    const data = (raw as any).data ?? raw;
    if (Array.isArray(data)) return data;
    // Si viene como { requisitos: [...] }
    if (Array.isArray((data as any).requisitos)) return (data as any).requisitos;
    // Si viene como { data: [...] } dentro de data
    if (Array.isArray((data as any).data)) return (data as any).data;
    // Si viene en otras variantes, intentar recoger arrays dentro del objeto
    for (const key of Object.keys(data || {})) {
      if (Array.isArray((data as any)[key])) return (data as any)[key];
    }
    return [];
  })();

  const isLoading = uploadMutation.isLoading || registerExistingMutation.isLoading;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const triggerFileSelect = () => fileInputRef.current?.click();
  const triggerSelectPending = () => setShowPendientesModal(true);

  useEffect(() => { if (formData.archivo) setShowOptionsModal(false); }, [formData.archivo]);

  const toUTCDate = (dateStr: string) => {
    const [y, m, d] = (dateStr || '').split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(Date.UTC(y, (m - 1), d, 0, 0, 0));
  };
  const diffDaysUTC = (fromStr: string, toStr: string) => {
    const from = toUTCDate(fromStr);
    const to = toUTCDate(toStr);
    if (!from || !to) return NaN;
    const ms = to.getTime() - from.getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  };
  const getEstadoFromVencimiento = (vencStr?: string) => {
    if (!vencStr) return 'sin_fecha';
    const hoy = new Date();
    const hoyUTC = new Date(Date.UTC(hoy.getUTCFullYear(), hoy.getUTCMonth(), hoy.getUTCDate()));
    const vencUTC = toUTCDate(vencStr);
    if (!vencUTC) return 'sin_fecha';
    const diff = Math.round((vencUTC.getTime() - hoyUTC.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'vencido';
    const THRESHOLD_POR_VENCER = 30;
    if (diff <= THRESHOLD_POR_VENCER) return 'por_vencer';
    return 'vigente';
  };

  useEffect(() => {
    const { fecha_emision_documento, fecha_vencimiento_documento, fecha_vencimiento_indefinido } = formData as any;
    let nextDias = formData.dias_validez_documento;

    if (!fecha_vencimiento_indefinido && fecha_emision_documento && fecha_vencimiento_documento) {
      const days = diffDaysUTC(fecha_emision_documento, fecha_vencimiento_documento);
      if (!Number.isNaN(days)) {
        const daysStr = String(days);
        if (daysStr !== formData.dias_validez_documento) nextDias = daysStr;
      }
    } else if (fecha_vencimiento_indefinido) {
      nextDias = '';
    }

    const nextEstado = fecha_vencimiento_indefinido ? 'sin_fecha' : getEstadoFromVencimiento(formData.fecha_vencimiento_documento || undefined);
    if (nextDias !== formData.dias_validez_documento || nextEstado !== formData.estado_documento) {
      setFormData((prev) => ({ ...prev, dias_validez_documento: nextDias, estado_documento: nextEstado }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fecha_emision_documento, formData.fecha_vencimiento_documento, (formData as any).fecha_vencimiento_indefinido]);

  const handleInputChange = (field: string, value: string | File | null) => {
    setFormData((prev) => ({ ...prev, [field]: value as any }));
    setErrors([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleInputChange('archivo', file);
  };

  const resetModalState = () => {
    setFormData(initialFormState());
    setSelectedClientId(null);
    setSelectedClientPrereq(null);
    setSelectedPendiente(null);
    setErrors([]);
    setUploadProgress(0);
    setIsCompressing(false);
    setShowOptionsModal(false);
    setShowPendientesModal(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleClose = () => { resetModalState(); onClose(); };

  // Apply initial prefill values when modal opens via prerrequisito selection
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      ...initialFormState(),
      nombre_documento: initialNombreDocumento || '',
      tipo_documento: initialTipoDocumento || '',
    });
    setSelectedClientId(initialClientId ?? null);
    setSelectedClientPrereq(initialClientPrereq ?? null);
    setSelectedPendiente(null);
    setErrors([]);
    setUploadProgress(0);
    setIsCompressing(false);
    setShowOptionsModal(false);
    setShowPendientesModal(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rutPersona, initialClientId, initialClientPrereq, initialNombreDocumento, initialTipoDocumento]);

  // If the modal was opened with a nombre/tipo but no explicit prereq id,
  // try to match the clientePrereqs list and auto-select the matching prereq.
  useEffect(() => {
    if (!isOpen) return;
    if ((initialClientPrereq === undefined || initialClientPrereq === null) && (initialNombreDocumento || initialTipoDocumento) && selectedClientId) {
      const nameToMatch = (initialNombreDocumento || '').toString().toLowerCase();
      const tipoToMatch = (initialTipoDocumento || '').toString().toLowerCase();
      for (const p of clientePrereqs) {
        const rawId = p.id ?? p.prerequisito_id ?? p.requisito_id ?? p._id ?? null;
        const pid = rawId === null || typeof rawId === 'undefined' ? null : (Number.isNaN(Number(rawId)) ? String(rawId) : Number(rawId));
        const label = (p.nombre || p.tipo_documento || p.tipo || p.descripcion || p.title || '').toString().toLowerCase();
        const tipo = (p.tipo_documento || p.tipo || '').toString().toLowerCase();
        if (nameToMatch && label.includes(nameToMatch)) {
          setSelectedClientPrereq(pid as any);
          return;
        }
        if (tipoToMatch && tipoToMatch === tipo) {
          setSelectedClientPrereq(pid as any);
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientePrereqs, initialNombreDocumento, initialTipoDocumento, selectedClientId]);

  // Note: general requisitos picker removed — only client-specific requisitos allowed

  // El requisito desde el que se abrió la ventana, si vino uno (solo para mostrarlo y derivar
  // el tipo_documento libre; la cobertura la decide el tipo del catálogo).
  const prereqOrigen = (() => {
    if (!selectedClientPrereq) return null;
    return clientePrereqs.find((p: any) => {
      const rawId = p.id ?? p.prerequisito_id ?? p.requisito_id ?? p._id ?? null;
      const pid = rawId === null || typeof rawId === 'undefined' ? null : (Number.isNaN(Number(rawId)) ? String(rawId) : Number(rawId));
      return pid === selectedClientPrereq;
    }) || null;
  })();

  // Helper: derive tipo_documento from selected client prerrequisito (if any)
  const deriveTipoDocumentoFromSelectedPrereq = () => {
    if (!prereqOrigen) return undefined;
    return prereqOrigen.tipo_documento || prereqOrigen.tipo || prereqOrigen.name || prereqOrigen.nombre || undefined;
  };

  // Id del documento recién creado, en las dos formas de respuesta: POST /documentos devuelve
  // data.documentos[], el registro de un pendiente devuelve data.id.
  const extraerIdNuevo = (resp: any): number | null => {
    const d = resp?.data ?? resp;
    const id = Array.isArray(d?.documentos) ? d.documentos[0]?.id : d?.id;
    return id ? Number(id) : null;
  };

  // Manda el documento reemplazado al historial. Si falla, la subida ya está hecha y no se
  // revierte: solo se avisa. 'ya_inactivo' (el backend ya lo archivó solo al subir) y
  // 'sin_reemplazo' (reutilizó el mismo archivo por huella) no son errores.
  const archivarReemplazado = async (resp: any) => {
    if (!reemplazaDocumentoId) return;
    const nuevoId = extraerIdNuevo(resp);
    if (!nuevoId) {
      toast.warning('Se subió el documento, pero no se pudo pasar la versión anterior al historial.');
      return;
    }
    if (nuevoId === Number(reemplazaDocumentoId)) return;
    const prereqId = Number(selectedClientPrereq);
    try {
      const r = await apiService.archivarDocumentoAnterior(nuevoId, Number(reemplazaDocumentoId), Number.isInteger(prereqId) ? prereqId : null);
      // El anterior era otro papel que acredita más requisitos: se movió solo este y sigue vigente.
      if (r?.parcial) toast.info('El documento anterior sigue vigente porque acredita otros requisitos.');
    } catch (err: any) {
      const motivo = err?.response?.data?.motivo;
      if (motivo === 'ya_inactivo' || motivo === 'sin_reemplazo') return;
      console.error('Error archivando la versión anterior:', err, err?.response?.data);
      toast.warning('Se subió el documento, pero la versión anterior sigue vigente.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Reglas básicas
    if (!formData.nombre_documento || formData.nombre_documento.trim() === '') {
      setErrors(['El nombre del documento es obligatorio.']);
      return;
    }
    if (!formData.archivo && !selectedPendiente) {
      setErrors(['Debes seleccionar un archivo (desde tu equipo o desde pendientes).']);
      return;
    }
    // No mezclar local + pendiente
    if (formData.archivo && selectedPendiente) {
      setErrors(['No puedes subir un archivo y seleccionar uno pendiente al mismo tiempo.']);
      return;
    }

    try {
        if (formData.archivo) {
        const tipoDoc = deriveTipoDocumentoFromSelectedPrereq() || formData.tipo_documento || 'documento_personal';

        let archivoFinal = formData.archivo;
        if (isCompressibleImage(archivoFinal)) {
          setIsCompressing(true);
          try {
            archivoFinal = await compressImageFile(archivoFinal);
          } finally {
            setIsCompressing(false);
          }
        }

        const data: CreateDocumentoData = {
          personal_id: rutPersona,
          nombre_documento: formData.nombre_documento,
          tipo_documento: tipoDoc,
          archivo: archivoFinal,
          fecha_emision: formData.fecha_emision_documento || undefined,
          fecha_vencimiento: formData.fecha_vencimiento_documento || undefined,
          dias_validez: formData.dias_validez_documento ? parseInt(formData.dias_validez_documento) : undefined,
          estado_documento: formData.estado_documento || undefined,
          institucion_emisora: formData.institucion_emisora || undefined,
        };
        if (selectedClientPrereq !== null && typeof selectedClientPrereq !== 'undefined') {
          data.prerrequisitos = [selectedClientPrereq];
          data.cliente_id = selectedClientId || undefined;
        }

        const docErrors = validateDocumentoData(data);
        if (docErrors.length > 0) { setErrors(docErrors); return; }
        const payload = createDocumentoFormData(data);
        const resp = await uploadMutation.mutateAsync(payload);
        await archivarReemplazado(resp);
        // Después de subir, forzar refresco de documentos de la persona
        try {
          await refetchDocumentosPersona();
        } catch (e) {
          console.warn('No se pudo refetchear documentos tras upload:', e);
        }
      }
      if (selectedPendiente) {
        const resp = await registerExistingMutation.mutateAsync({
          rut_persona: rutPersona,
          file: selectedPendiente.file,
          nombre_documento: formData.nombre_documento,
          tipo_documento: deriveTipoDocumentoFromSelectedPrereq() || formData.tipo_documento || undefined,
          fecha_emision: formData.fecha_emision_documento || undefined,
          fecha_vencimiento: formData.fecha_vencimiento_documento || undefined,
          dias_validez: formData.dias_validez_documento ? parseInt(formData.dias_validez_documento) : undefined,
          estado_documento: formData.estado_documento || undefined,
          institucion_emisora: formData.institucion_emisora || undefined,
          cliente_id: selectedClientId || undefined,
          prerrequisitos: selectedClientPrereq ? [selectedClientPrereq] : undefined,
        });
        await archivarReemplazado(resp);
        await refetchDocumentosPersona();
      }

      onSuccess();
      resetModalState();
      onClose();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error al subir documento:', err, err?.response?.data);
      // El backend suele devolver el motivo real en response.data; err.message de axios
      // por sí solo es genérico ("Request failed with status code 400").
      const backendMsg = err?.response?.data?.message || err?.response?.data?.error;
      const mensaje = backendMsg
        ? `${backendMsg}`
        : (err?.message || 'No se pudo subir el documento.');
      setErrors([mensaje]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg md:max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-hide">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white p-5 rounded-t-xl flex items-start justify-between">
          <div className="flex items-center">
            <FileText className="h-6 w-6 mr-3" />
            <div>
              <h2 className="text-xl font-bold">{reemplazaDocumentoId ? 'Reemplazar Documento' : 'Subir Documento'}</h2>
              <p className="text-orange-100 text-sm">
                {nombrePersona} ({rutPersona})
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-white hover:text-orange-200 transition-colors p-1 rounded-full hover:bg-white/20" disabled={isLoading}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {errors.length > 0 && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <ul className="list-disc list-inside space-y-1">
                {errors.map((e) => (<li key={e}>{e}</li>))}
              </ul>
            </div>
          )}

          <div className="space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Documento *</label>
              <input
                type="text"
                value={formData.nombre_documento}
                onChange={(e) => handleInputChange('nombre_documento', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Ej: Contrato de Trabajo 2024"
                required
                disabled={isLoading}
              />
            </div>

            {/* Si la ventana se abrió desde la tarjeta de un requisito, se muestra cuál. */}
            {prereqOrigen && (
              <p className="text-xs text-gray-500">
                Se subió desde el requisito{' '}
                <span className="font-medium text-gray-700">
                  {prereqOrigen.nombre || prereqOrigen.tipo_documento || prereqOrigen.tipo || prereqOrigen.descripcion}
                </span>.
              </p>
            )}

            {reemplazaDocumentoId && (
              <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                Reemplaza a <span className="font-medium">«{reemplazaNombre || 'el documento actual'}»</span>.
                La versión actual quedará en el historial y sus asociaciones pasan al documento nuevo.
              </p>
            )}


            {/* Subida/selección */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Archivo</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-orange-400 transition-colors">
                <div className="space-y-1 text-center relative">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setShowOptionsModal(true)}
                      className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-orange-600 hover:bg-gray-50 focus:outline-none"
                      disabled={isLoading}
                    >
                      Subir archivo
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">PDF, Word, Excel, PowerPoint, TXT, RTF o imagen (JPG/PNG/TIFF/BMP) hasta {FILE_CONFIG.MAX_SIZE_MB}MB</p>
                  <input ref={fileInputRef} id="file-upload-doc" name="file-upload-doc" type="file" className="sr-only" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.jpg,.jpeg,.png,.tiff,.bmp" onChange={handleFileChange} disabled={isLoading} />
                </div>
              </div>
              {formData.archivo && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800 flex items-center">
                    <FileText className="h-4 w-4 mr-2" /> {formData.archivo.name}
                  </p>
                </div>
              )}
              {!formData.archivo && selectedPendiente && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between">
                  <p className="text-sm text-amber-800 flex items-center">
                    <FileText className="h-4 w-4 mr-2" /> Archivo seleccionado (Drive): {selectedPendiente.displayName}
                  </p>
                  <button type="button" onClick={() => setSelectedPendiente(null)} className="text-xs px-2 py-1 border rounded hover:bg-amber-100">Quitar</button>
                </div>
              )}
            </div>

            {/* Validez */}
            <div className="border-t pt-4">
              <h3 className="text-md font-medium text-gray-900 mb-4">Información de Validez</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Emisión</label>
                  <input type="date" value={formData.fecha_emision_documento} onChange={(e) => handleInputChange('fecha_emision_documento', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" disabled={isLoading} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Vencimiento</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="date"
                      value={formData.fecha_vencimiento_documento}
                      onChange={(e) => handleInputChange('fecha_vencimiento_documento', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      disabled={isLoading || !!(formData as any).fecha_vencimiento_indefinido}
                    />
                    <label className="inline-flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={(formData as any).fecha_vencimiento_indefinido}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({ ...prev, fecha_vencimiento_indefinido: checked, fecha_vencimiento_documento: checked ? '' : prev.fecha_vencimiento_documento }));
                        }}
                        className="mr-2"
                        disabled={isLoading}
                      />
                      Indefinido
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Días de Validez</label>
                  <input type="number" min="1" value={formData.dias_validez_documento} onChange={(e) => handleInputChange('dias_validez_documento', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder={formData.fecha_emision_documento && formData.fecha_vencimiento_documento ? 'Calculado automáticamente' : 'Ej: 365'} disabled={isLoading || (!!formData.fecha_emision_documento && !!formData.fecha_vencimiento_documento)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado del Documento</label>
                  <select value={formData.estado_documento} onChange={(e) => handleInputChange('estado_documento', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" disabled={isLoading}>
                    <option value="">Seleccionar estado</option>
                    <option value="vigente">Vigente</option>
                    <option value="vencido">Vencido</option>
                    <option value="por_vencer">Por Vencer</option>
                    <option value="sin_fecha">Sin Fecha</option>
                  </select>
                </div>
              </div>
              {uploadCategory !== 'personal' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Institución Emisora</label>
                  <input type="text" value={formData.institucion_emisora} onChange={(e) => handleInputChange('institucion_emisora', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Ej: Ministerio del Trabajo, SII, etc." disabled={isLoading} />
                </div>
              )}
            </div>
          </div>

          {(isCompressing || (isLoading && uploadProgress > 0)) && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{isCompressing ? 'Optimizando imagen...' : 'Subiendo archivo...'}</span>
                {!isCompressing && <span>{uploadProgress}%</span>}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-orange-500 h-2 rounded-full transition-all duration-200"
                  style={{ width: isCompressing ? '100%' : `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-4 pt-4 border-t">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" disabled={isLoading || isCompressing}>Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading || isCompressing}>
              {isCompressing
                ? (<><LoadingSpinner /><span className="ml-2">Optimizando...</span></>)
                : isLoading
                  ? (<><LoadingSpinner /><span className="ml-2">Subiendo...</span></>)
                  : (<><Save className="h-4 w-4 mr-2" /> Subir Documento</>)
              }
            </button>
          </div>
        </form>
      </div>

      {/* Ventanas auxiliares */}
      <UploadOptionsModal isOpen={showOptionsModal} onClose={() => setShowOptionsModal(false)} onSubirDesdeEquipo={triggerFileSelect} onSeleccionarPendiente={triggerSelectPending} />
    <PendientesRegistroModal
      isOpen={showPendientesModal}
      onClose={() => setShowPendientesModal(false)}
      documentos={documentosLocales} /* documentos generales (no cursos) */
      existingDocs={existingDocs}
      onSelect={async (f, displayName) => {
            // Registro inmediato del archivo seleccionado como existente
            setShowPendientesModal(false);
            setSelectedPendiente({ file: f, displayName });

            try {
              const tipoDoc = deriveTipoDocumentoFromSelectedPrereq() || formData.tipo_documento || 'documento_personal';
              const resp = await registerExistingMutation.mutateAsync({
                rut_persona: rutPersona,
                file: f,
                nombre_documento: formData.nombre_documento || displayName || (f.nombre_original || f.nombre_archivo || f.name || f.title),
                tipo_documento: tipoDoc,
                fecha_emision: formData.fecha_emision_documento || undefined,
                fecha_vencimiento: formData.fecha_vencimiento_documento || undefined,
                dias_validez: formData.dias_validez_documento ? parseInt(formData.dias_validez_documento) : undefined,
                estado_documento: formData.estado_documento || undefined,
                institucion_emisora: formData.institucion_emisora || undefined,
                cliente_id: selectedClientId || undefined,
                prerrequisitos: selectedClientPrereq ? [selectedClientPrereq] : undefined,
              });
              await archivarReemplazado(resp);

              // Refrescar documentos de la persona para que el UI muestre el nuevo registro
              await refetchDocumentosPersona();
            } catch (err: any) {
              // Mostrar error en pantalla
              // eslint-disable-next-line no-console
              console.error('Error al registrar pendiente automáticamente:', err);
              setErrors([err?.message || 'Error al registrar el documento pendiente.']);
              // dejar selectedPendiente para permitir reintento manual
            }
          }}
      />
    </div>
  );
};

export default SubirDocumentoModal;
